import React, { useState } from 'react';
import { DocumentType, UploadDocumentRequest } from '../../types/document';
import { CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { documentsAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

interface UploadDocumentFormProps {
  playerId?: number;
  userId?: number;
  isPublic?: boolean;
  onSuccess: () => void;
  onCancel?: () => void;
}

/** Beágyazható dokumentumfeltöltő űrlap (nem modal). */
const UploadDocumentForm: React.FC<UploadDocumentFormProps> = ({
  playerId,
  userId,
  isPublic = false,
  onSuccess,
  onCancel,
}) => {
  const { showToast } = useToast();
  const [files, setFiles] = useState<File[]>([]);
  const [description, setDescription] = useState('');
  const [type, setType] = useState<DocumentType>(DocumentType.Other);
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (files.length === 0) return;

    setUploading(true);
    try {
      const request: UploadDocumentRequest = {
        type,
        description: description || undefined,
        playerId,
        userId,
        isPublic,
      };

      for (const file of files) {
        await documentsAPI.uploadDocument(file, request);
      }

      showToast(files.length > 1 ? `${files.length} dokumentum feltöltve` : 'Dokumentum feltöltve');
      setFiles([]);
      setDescription('');
      setType(DocumentType.Other);
      onSuccess();
    } catch (error) {
      console.error('Failed to upload document:', error);
      showToast('Hiba történt a fájl(ok) feltöltése során', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFiles(Array.from(e.target.files));
    }
  };

  const removeFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const documentTypes = [
    { value: DocumentType.Photo, label: 'Fénykép' },
    { value: DocumentType.MedicalCertificate, label: 'Orvosi igazolás' },
    { value: DocumentType.RegistrationForm, label: 'Regisztrációs lap' },
    { value: DocumentType.TransferDocument, label: 'Átigazolás' },
    { value: DocumentType.Contract, label: 'Szerződés' },
    { value: DocumentType.ExcuseLetter, label: 'Kikérő' },
    { value: DocumentType.AbsenceCertificate, label: 'Hiányzás igazolás' },
    { value: DocumentType.PeExemption, label: 'Testnevelés felmentés' },
    { value: DocumentType.Other, label: 'Egyéb' },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Fájlok */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Fájlok *</label>
        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-primary-400 transition-colors">
          <div className="space-y-1 text-center">
            <CloudArrowUpIcon className="mx-auto h-10 w-10 text-gray-400" />
            <div className="flex text-sm text-gray-600">
              <label
                htmlFor="file-upload"
                className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
              >
                <span>Fájlok kiválasztása</span>
                <input
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  multiple
                  className="sr-only"
                  onChange={handleFileChange}
                />
              </label>
              <p className="pl-1">vagy húzd ide</p>
            </div>
            <p className="text-xs text-gray-500">PDF, PNG, JPG, DOCX</p>
          </div>
        </div>

        {files.length > 0 && (
          <div className="mt-3 space-y-2">
            <p className="text-sm font-medium text-gray-700">
              Kiválasztott fájlok ({files.length}):
            </p>
            <ul className="space-y-1">
              {files.map((file, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded"
                >
                  <span className="truncate">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="ml-2 flex h-9 w-9 shrink-0 items-center justify-center text-red-600 hover:text-red-800"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Típus */}
      <div>
        <label htmlFor="doc-type" className="block text-sm font-medium text-gray-700">
          Típus *
        </label>
        <select
          id="doc-type"
          value={type}
          onChange={(e) => setType(parseInt(e.target.value) as DocumentType)}
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
          required
        >
          {documentTypes.map((dt) => (
            <option key={dt.value} value={dt.value}>
              {dt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Leírás */}
      <div>
        <label htmlFor="doc-description" className="block text-sm font-medium text-gray-700">
          Leírás
        </label>
        <textarea
          id="doc-description"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
          placeholder="Opcionális megjegyzés a dokumentumról..."
        />
      </div>

      <div className="flex flex-col gap-2 md:flex-row-reverse md:gap-3">
        <button
          type="submit"
          disabled={uploading || files.length === 0}
          className="w-full md:w-auto inline-flex justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? `Feltöltés... (${files.length} fájl)` : 'Feltöltés'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={uploading}
            className="w-full md:w-auto inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
          >
            Mégse
          </button>
        )}
      </div>
    </form>
  );
};

export default UploadDocumentForm;
